/**
 * TabularGrid — App-mode editor for a tabular section with Operator
 * spreadsheet density (Variant A): hairline cells, compact rows, primary
 * headers, mono numbers, calculator/delete on focus|hover only.
 * Behavior: client-side sort + multi-level group-by (via buildTabularView),
 * show/hide columns, per-column resize, Σ aggregates footer and typed inline
 * cell editors (number → FormattedNumberField, ref → RefFieldWidget, …).
 *
 * Invariants:
 * - Sort/group are a lens only: edits always write back into the ORIGINAL
 *   `rows` array at `sourceIndex`; the persisted array is never reordered.
 * - expandedGroups: empty ⇒ expand-all. First collapse seeds the whitelist
 *   with all currently-visible group keys, then drops the collapsed one, so
 *   sibling groups don't snap back to expanded.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import CalculateIcon from '@mui/icons-material/Calculate'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTheme, alpha } from '@mui/material/styles'
import { MONO_FONT } from '@/shared/typography'
import { tabularHeaderTypeSx } from '@/shared/form/formChrome'
import { DateTimeField } from '@/shared/DateTimeField'
import { resolveDateTimeFieldMode } from '@/shared/fieldInputTypes'
import type { AttributeDef } from '../../designer/services/configObjectApi'
import { FormattedNumberField, formatNumberDisplay, resolveNumberDigits } from '../number'
import { RefFieldWidget } from '../RefFieldWidget'
import { CompositeFieldWidget } from '../CompositeFieldWidget'
import { ListAggregatesFooter } from '../ListAggregatesFooter'
import { computeClientAggregates } from '../computeClientAggregates'
import {
  aggregatesForField,
  formatAggregateValue,
  parseAggregatePair,
} from '@/shared/listViews/aggregatesLayout'
import { buildTabularView, cycleSort, type TabularViewNode } from './tabularViewModel'
import { useTabularPrefs } from './useTabularPrefs'
import { autoFitWidth, clampColWidth, tabularMinWidth, type AutoFitKind } from './columnLayout'
import { TabularToolbar } from './TabularToolbar'
import { TabularImportDialog } from './TabularImportDialog'
import {
  TABULAR_COL_HEADER_CLASS,
  tabularContainerSx,
  tabularGridPaneSx,
  tabularHInnerSx,
  tabularHScrollSx,
  tabularMonoInputSx,
  tabularScrollBodySx,
  tabularTableSx,
} from './tabularCellChrome'
import { FORM_CHROME_DENSITY } from '@/shared/form/formChrome'
import { TabularSectionTitle } from '@/shared/components/TabularSectionTitle'

export interface TabularColumn {
  name: string
  title: string
  /** Full attribute descriptor — drives the cell widget (ref target, number scale…). */
  attr: AttributeDef
  /** Floor passed to auto-fit (Task 3 stamps 88 on number columns). */
  minWidth?: number
}

export interface TabularGridProps {
  columns: TabularColumn[]
  rows: Record<string, unknown>[]
  onChange: (next: Record<string, unknown>[]) => void
  connectionId: string
  objectName: string
  tabularName: string
  disabled?: boolean
  onOpenRef?: (target: string, id: string) => void
  title?: string
  /** Fired when a cell's value is COMMITTED (blur for text/number, immediately
   * for ref/date/bool). rowIndex = source index into the canonical rows array;
   * nextRows = the rows array after the edit. */
  onCellCommit?: (rowIndex: number, colName: string, nextRows: Record<string, unknown>[]) => void
}

const REF_TYPES = new Set(['ref', 'enum_ref', 'composite_ref', 'reference'])

/** Same end-slot as FormattedNumberField's calculator — keeps Σ digits on the cell value. */
function TabularCalcReserve() {
  return (
    <InputAdornment
      position="end"
      aria-hidden
      data-column-calc-reserve
      sx={{ visibility: 'hidden', pointerEvents: 'none' }}
    >
      <IconButton size="small" tabIndex={-1}>
        <CalculateIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  )
}

function resolveType(attr: AttributeDef): string {
  const raw = String(attr.attr_type || 'string').toLowerCase()
  return raw === 'reference' ? 'ref' : raw
}

function refTarget(attr: AttributeDef): string {
  const p = (attr.props ?? {}) as Record<string, unknown>
  const t = p.target ?? p.referenceTarget ?? p.reference_target
  return typeof t === 'string' ? t : ''
}

function collectAutoFitSamples(
  col: TabularColumn,
  rows: Record<string, unknown>[],
): { samples: string[]; kind: AutoFitKind } {
  const numeric = resolveType(col.attr) === 'number'
  if (!numeric) {
    return {
      samples: rows.slice(0, 50).map((r) => String(r[col.name] ?? '')),
      kind: 'text',
    }
  }
  const { precision, scale } = resolveNumberDigits(col.attr.props)
  const samples: string[] = []
  for (const r of rows.slice(0, 50)) {
    const n = Number(r[col.name])
    if (!Number.isFinite(n)) continue
    samples.push(formatNumberDisplay(n, precision, scale))
  }
  const worst = 10 ** Math.min(precision - scale, 7) - 10 ** -scale
  samples.push(formatNumberDisplay(worst, precision, scale))
  return { samples, kind: 'number' }
}

export function TabularGrid({
  columns,
  rows,
  onChange,
  connectionId,
  objectName,
  tabularName,
  disabled = false,
  onOpenRef,
  title,
  onCellCommit,
}: TabularGridProps): JSX.Element {
  const theme = useTheme()
  const { prefs, updatePrefs, resetPrefs } = useTabularPrefs(connectionId, objectName, tabularName)
  const [importOpen, setImportOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Blur-commit cells (number/text) buffer their own keystrokes and call
  // `updateCell` only from their OWN blur handler — which fires in the SAME
  // event dispatch as the ancestor Box's bubble-phase onBlur below, before
  // React flushes the resulting state update. The `rows` prop closure read at
  // that point would therefore still be one edit stale. `latestRowsRef` is
  // kept in sync on every render AND synchronously inside `updateCell`, so
  // the ancestor's onBlur always sees the value that was just committed.
  const latestRowsRef = useRef(rows)
  // Edit marks (`editedCellsRef`, declared below) are positional
  // (`sourceIndex:col`) — they only mean anything against the array they
  // were recorded on. If `rows` arrives with a NEW identity that this grid
  // did NOT itself just produce via onChange (e.g. a field-event round-trip
  // replaced the whole tabular part wholesale while a DIFFERENT cell was
  // mid-edit), any pending mark refers to a position in the OLD array and
  // must be dropped — otherwise a later no-edit blur on that same position
  // fires a spurious commit against unrelated data. `lastEmittedRowsRef`
  // records what WE last produced via onChange, so a grid-originated update
  // (which round-trips straight back as the next `rows` prop) is never
  // mistaken for an external replacement.
  const lastEmittedRowsRef = useRef(rows)
  const editedCellsRef = useRef<Set<string>>(new Set())
  if (rows !== latestRowsRef.current && rows !== lastEmittedRowsRef.current) {
    editedCellsRef.current.clear()
  }
  latestRowsRef.current = rows

  const visibleColumns = useMemo(
    () => columns.filter((c) => !prefs.hidden.includes(c.name)),
    [columns, prefs.hidden],
  )
  const visibleKey = visibleColumns.map((c) => c.name).join('|')

  // Auto-fit any visible column that has no stored width yet (mount + column-set change).
  useEffect(() => {
    const missing = visibleColumns.filter((c) => prefs.widths[c.name] == null)
    if (missing.length === 0) return
    const patch: Record<string, number> = {}
    for (const c of missing) {
      const { samples, kind } = collectAutoFitSamples(c, rows)
      patch[c.name] = autoFitWidth(c.title, samples, { kind, minWidth: c.minWidth })
    }
    updatePrefs({ widths: { ...prefs.widths, ...patch } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey])

  const fitAll = useCallback(() => {
    const widths: Record<string, number> = { ...prefs.widths }
    for (const c of visibleColumns) {
      const { samples, kind } = collectAutoFitSamples(c, rows)
      widths[c.name] = autoFitWidth(c.title, samples, { kind, minWidth: c.minWidth })
    }
    updatePrefs({ widths })
  }, [prefs.widths, visibleColumns, rows, updatePrefs])

  const searchFields = useMemo(() => columns.map((c) => c.name), [columns])
  const view = useMemo(
    () =>
      buildTabularView({
        rows,
        sort: prefs.sort,
        groupBy: prefs.groupBy,
        expandedGroups: prefs.expandedGroups,
        search,
        searchFields,
      }),
    [rows, prefs.sort, prefs.groupBy, prefs.expandedGroups, search, searchFields],
  )

  const toggleSort = (name: string) => updatePrefs({ sort: cycleSort(prefs.sort, name) })

  const toggleColumn = (name: string) => {
    const next = prefs.hidden.includes(name)
      ? prefs.hidden.filter((n) => n !== name)
      : [...prefs.hidden, name]
    updatePrefs({ hidden: next })
  }

  const toggleGroup = (key: string) => {
    const expanded = prefs.expandedGroups.length === 0 ? true : prefs.expandedGroups.includes(key)
    if (expanded) {
      if (prefs.expandedGroups.length === 0) {
        // First collapse: materialize every currently-visible group key so
        // siblings stay expanded, then drop the one we're collapsing.
        const allKeys = view.filter((n): n is Extract<TabularViewNode, { kind: 'group' }> => n.kind === 'group').map((n) => n.key)
        updatePrefs({ expandedGroups: allKeys.filter((k) => k !== key) })
      } else {
        updatePrefs({ expandedGroups: prefs.expandedGroups.filter((k) => k !== key) })
      }
    } else {
      updatePrefs({ expandedGroups: [...prefs.expandedGroups, key] })
    }
  }

  // --- Write path: edits map through sourceIndex into the original array. ---
  const updateCell = (sourceIndex: number, col: string, value: unknown): Record<string, unknown>[] => {
    const next = rows.slice()
    next[sourceIndex] = { ...next[sourceIndex], [col]: value }
    editedCellsRef.current.add(`${sourceIndex}:${col}`)
    latestRowsRef.current = next
    lastEmittedRowsRef.current = next
    onChange(next)
    return next
  }
  // Composite cells write TWO keys (value + `${col}_type`) from ONE selection —
  // must emit exactly one onChange round, not two `updateCell` calls, or the
  // second call's `rows` closure would still be one edit stale. Only the
  // primary column is marked edited: `commitCell` keys off it, matching the
  // other immediate (ref/date/bool) cell widgets above.
  const updateRowFields = (
    sourceIndex: number,
    patch: Record<string, unknown>,
    primaryCol: string,
  ): Record<string, unknown>[] => {
    const next = rows.slice()
    next[sourceIndex] = { ...next[sourceIndex], ...patch }
    editedCellsRef.current.add(`${sourceIndex}:${primaryCol}`)
    latestRowsRef.current = next
    lastEmittedRowsRef.current = next
    onChange(next)
    return next
  }
  const commitCell = (sourceIndex: number, col: string, nextRows: Record<string, unknown>[]) => {
    if (editedCellsRef.current.delete(`${sourceIndex}:${col}`)) {
      onCellCommit?.(sourceIndex, col, nextRows)
    }
  }

  const removeRow = (sourceIndex: number) => {
    const next = rows.filter((_, i) => i !== sourceIndex)
    latestRowsRef.current = next
    lastEmittedRowsRef.current = next
    onChange(next)
  }

  const scrollRef = useRef<HTMLDivElement>(null)

  const addRow = () => {
    const current = latestRowsRef.current
    const blank: Record<string, unknown> = {}
    for (const c of columns) {
      const t = resolveType(c.attr)
      if (t === 'boolean') blank[c.name] = false
      else if (t === 'number') blank[c.name] = null
      else blank[c.name] = ''
    }
    blank.line_number = current.length + 1
    const next = [...current, blank]
    latestRowsRef.current = next
    lastEmittedRowsRef.current = next
    // Sort (asc on a string / desc on line_number) would park the blank
    // above existing rows. Add is always "new line at the end".
    if (prefs.sort) updatePrefs({ sort: null })
    onChange(next)
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    })
  }

  // --- Column resize (mousedown on header border → drag → persist width). ---
  const dragRef = useRef<{ name: string; startX: number; startWidth: number } | null>(null)
  const beginResize = (name: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { name, startX: e.clientX, startWidth: prefs.widths[name] ?? 120 }
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const w = clampColWidth(d.startWidth + (ev.clientX - d.startX))
      updatePrefs({ widths: { ...prefs.widths, [d.name]: w } })
    }
    const onUp = () => {
      dragRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const numericFields = useMemo(
    () =>
      columns
        .filter((c) => resolveType(c.attr) === 'number')
        .map((c) => {
          const precision = Number(c.attr.props?.precision)
          const scale = Number(c.attr.props?.scale)
          return {
            name: c.name,
            label: c.title,
            ...(Number.isFinite(precision) ? { precision } : {}),
            ...(Number.isFinite(scale) ? { scale } : {}),
          }
        }),
    [columns],
  )
  const leafRows = useMemo(
    () => view.filter((n): n is Extract<TabularViewNode, { kind: 'row' }> => n.kind === 'row').map((n) => n.row),
    [view],
  )
  const aggValues = useMemo(
    () => computeClientAggregates(leafRows, prefs.aggregates),
    [leafRows, prefs.aggregates],
  )

  const renderCell = (col: TabularColumn, row: Record<string, unknown>, sourceIndex: number) => {
    const t = resolveType(col.attr)
    const val = row[col.name]
    const compositeTypes = col.attr.props?.composite_types
    if (t === 'composite_ref' && Array.isArray(compositeTypes) && compositeTypes.length > 0) {
      const typeCol = `${col.name}_type`
      return (
        <CompositeFieldWidget
          attr={col.attr}
          value={val ?? null}
          typeValue={row[typeCol] ?? null}
          onChangePair={(v, t2) =>
            commitCell(
              sourceIndex,
              col.name,
              updateRowFields(sourceIndex, { [col.name]: v, [typeCol]: t2 }, col.name),
            )
          }
          disabled={disabled}
          compact
          connectionId={connectionId}
          onOpenRef={onOpenRef}
        />
      )
    }
    if (REF_TYPES.has(t)) {
      return (
        <RefFieldWidget
          attr={col.attr}
          value={val ?? null}
          onChange={(v) => commitCell(sourceIndex, col.name, updateCell(sourceIndex, col.name, v))}
          disabled={disabled}
          connectionId={connectionId}
          isRequired={Boolean(col.attr.required)}
          onOpenRef={onOpenRef}
          compact
        />
      )
    }
    if (t === 'number') {
      return (
        <Box sx={{ display: 'contents' }} onBlur={() => commitCell(sourceIndex, col.name, latestRowsRef.current)}>
          <FormattedNumberField
            value={val == null || val === '' ? null : Number(val)}
            onChange={(v) => commitCell(sourceIndex, col.name, updateCell(sourceIndex, col.name, v))}
            precision={Number(col.attr.props?.precision)}
            scale={Number(col.attr.props?.scale)}
            disabled={disabled}
            size="small"
            fullWidth
            variant="standard"
            calculatorVisibility="focus"
            inputProps={{ style: tabularMonoInputSx }}
          />
        </Box>
      )
    }
    if (t === 'boolean') {
      return (
        <Checkbox
          size="small"
          checked={Boolean(val)}
          onChange={(e) => commitCell(sourceIndex, col.name, updateCell(sourceIndex, col.name, e.target.checked))}
          disabled={disabled}
        />
      )
    }
    if (t === 'date' || t === 'datetime' || t === 'time') {
      const fmt = typeof col.attr.props?.format === 'string' ? col.attr.props.format : undefined
      const mode = resolveDateTimeFieldMode(t, fmt)
      if (mode) {
        return (
          <DateTimeField
            mode={mode}
            attrType={t}
            format={fmt}
            value={val}
            onChange={(next) => commitCell(sourceIndex, col.name, updateCell(sourceIndex, col.name, next))}
            disabled={disabled}
            variant="standard"
            size="small"
            fullWidth
          />
        )
      }
    }
    return (
      <Box sx={{ display: 'contents' }} onBlur={() => commitCell(sourceIndex, col.name, latestRowsRef.current)}>
        <TextField
          variant="standard"
          size="small"
          value={val ?? ''}
          onChange={(e) => updateCell(sourceIndex, col.name, e.target.value)}
          disabled={disabled}
          fullWidth
        />
      </Box>
    )
  }

  const heading = title ?? ''
  const colGroup = (
    <colgroup>
      {visibleColumns.map((c) => (
        <col
          key={c.name}
          style={prefs.widths[c.name] ? { width: prefs.widths[c.name] } : undefined}
        />
      ))}
      <col style={{ width: 40 }} />
    </colgroup>
  )
  const showColumnFooter = prefs.aggregatesLayout === 'columns' && prefs.aggregates.length > 0
  const tableMinWidth = tabularMinWidth(
    visibleColumns.map((c) => c.name),
    prefs.widths,
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      {heading ? <TabularSectionTitle title={heading} /> : null}
    <Box
      data-tabular-card
      sx={{
        borderRadius: `${FORM_CHROME_DENSITY.radiusCard}px`,
        boxShadow: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
    <Box
      onKeyDown={(e) => {
        // Insert produces no text, so adding a row is safe even with a cell focused.
        if (e.key === 'Insert' && !disabled) {
          e.preventDefault()
          addRow()
        }
      }}
      sx={tabularContainerSx(theme)}
    >
      <TabularToolbar
        columns={columns.map((c) => ({ name: c.name, title: c.title }))}
        groupBy={prefs.groupBy}
        onGroupByChange={(next) => updatePrefs({ groupBy: next, expandedGroups: [] })}
        hidden={prefs.hidden}
        onToggleColumn={toggleColumn}
        onResetColumns={resetPrefs}
        onFit={fitAll}
        onAddRow={addRow}
        onImportExcel={() => setImportOpen(true)}
        disabled={disabled}
        connectionId={connectionId}
        objectName={objectName}
        tabularName={tabularName}
        search={{ value: search, onChange: setSearch }}
      />

      <Box data-tabular-grid-pane sx={tabularGridPaneSx}>
        <Box data-tabular-h-scroll sx={tabularHScrollSx}>
        <Box data-tabular-h-inner sx={{ ...tabularHInnerSx, minWidth: tableMinWidth }}>
        <Table size="small" sx={{ ...tabularTableSx(theme), width: '100%' }}>
          {colGroup}
          <TableHead>
            <TableRow>
              {visibleColumns.map((c) => {
                const w = prefs.widths[c.name]
                const sorted = prefs.sort?.field === c.name ? prefs.sort.dir : null
                const numeric = resolveType(c.attr) === 'number'
                return (
                  <TableCell
                    key={c.name}
                    className={TABULAR_COL_HEADER_CLASS}
                    onClick={() => toggleSort(c.name)}
                    align={numeric ? 'right' : 'left'}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      width: w ? `${w}px` : undefined,
                      ...tabularHeaderTypeSx(theme),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: numeric ? 'flex-end' : 'flex-start',
                        gap: 0.25,
                        pr: 1,
                        color: 'inherit',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                      }}
                    >
                      {c.title}
                      {sorted === 'asc' && <ArrowDropUpIcon sx={{ fontSize: 16 }} />}
                      {sorted === 'desc' && <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
                    </Box>
                    <Box
                      role="separator"
                      aria-label={`Resize ${c.title}`}
                      onMouseDown={(e) => beginResize(c.name, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 6,
                        height: '100%',
                        cursor: 'col-resize',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.4) },
                      }}
                    />
                  </TableCell>
                )
              })}
              <TableCell className={TABULAR_COL_HEADER_CLASS} sx={{ width: 40, p: 0 }} />
            </TableRow>
          </TableHead>
        </Table>

        <TableContainer ref={scrollRef} data-tabular-scroll sx={tabularScrollBodySx}>
          <Table size="small" sx={{ ...tabularTableSx(theme), width: '100%' }}>
            {colGroup}
            <TableBody>
              {view.map((node) => {
                if (node.kind === 'group') {
                  return (
                    <TableRow key={`g:${node.key}`} hover>
                      <TableCell
                        colSpan={visibleColumns.length + 1}
                        onClick={() => toggleGroup(node.key)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: alpha(theme.palette.primary.main, 0.06),
                          fontWeight: 600,
                          pl: 1 + node.level * 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {node.collapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          <span>{node.label}</span>
                          <Typography component="span" sx={{ fontFamily: MONO_FONT, fontSize: 11, color: 'text.secondary', ml: 0.5 }}>
                            ({node.count})
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                }
                return (
                  <TableRow
                    key={`r:${node.sourceIndex}`}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        boxShadow: `inset 2px 0 0 ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    {visibleColumns.map((c) => (
                      <TableCell
                        key={c.name}
                        align={resolveType(c.attr) === 'number' ? 'right' : 'left'}
                      >
                        {renderCell(c, node.row, node.sourceIndex)}
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: 'right', width: 40 }}>
                      <IconButton
                        className="tabular-row-action"
                        size="small"
                        onClick={() => removeRow(node.sourceIndex)}
                        disabled={disabled}
                        aria-label="Delete row"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length + 1}
                    sx={{ textAlign: 'center', color: 'text.secondary', py: 2, height: 'auto' }}
                  >
                    No rows. Click "Add row" to start.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {showColumnFooter ? (
          <Table size="small" sx={{ ...tabularTableSx(theme), width: '100%' }}>
            {colGroup}
            <TableFooter>
              <TableRow data-tabular-column-footer>
                {visibleColumns.map((c) => {
                  const pairs = aggregatesForField(prefs.aggregates, c.name)
                  const numeric = resolveType(c.attr) === 'number'
                  return (
                    <TableCell
                      key={c.name}
                      data-column-footer={c.name}
                      align={numeric ? 'right' : 'left'}
                    >
                      {pairs.map((pair) => {
                        const { op } = parseAggregatePair(pair)
                        return (
                          <Box
                            key={pair}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: numeric ? 'flex-end' : 'flex-start',
                              width: '100%',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.5,
                                // Match `& .MuiInputBase-input { px: 1 }` so the digits
                                // sit where the input text sits, not on the adornment.
                                pr: numeric ? 1 : 0,
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                                {op}
                              </Typography>
                              <Typography
                                component="span"
                                data-column-footer-value
                                sx={{ fontFamily: MONO_FONT, fontSize: 12, fontWeight: 700 }}
                              >
                                {formatAggregateValue(aggValues[pair], c.attr.props, op)}
                              </Typography>
                            </Box>
                            {numeric ? <TabularCalcReserve /> : null}
                          </Box>
                        )
                      })}
                    </TableCell>
                  )
                })}
                <TableCell sx={{ width: 40, p: 0 }} />
              </TableRow>
            </TableFooter>
          </Table>
        ) : null}
        </Box>
        </Box>
      </Box>

      <ListAggregatesFooter
        recordCount={leafRows.length}
        aggregates={prefs.aggregates}
        values={aggValues}
        numericFields={numericFields}
        onChange={(next) => updatePrefs({ aggregates: next })}
        layout={prefs.aggregatesLayout}
        onLayoutChange={(next) => updatePrefs({ aggregatesLayout: next })}
        fieldOrder={prefs.aggregatesFieldOrder}
        onFieldOrderChange={(next) => updatePrefs({ aggregatesFieldOrder: next })}
        columnLayout
      />

      <TabularImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        connectionId={connectionId}
        objectName={objectName}
        columns={columns}
        existingRows={latestRowsRef.current}
        onApply={(next) => {
          lastEmittedRowsRef.current = next
          latestRowsRef.current = next
          onChange(next)
          setImportOpen(false)
        }}
      />
    </Box>
    </Box>
    </Box>
  )
}
